import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  Typography,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
  Button,
  FormControlLabel,
  Checkbox,
  Grid,
  FormControl,
  RadioGroup,
  Radio,
  InputLabel,
  Select,
  FormGroup,
  useTheme,
  ListItemIcon,
  ListItemText,
  MenuList,
  Alert
} from '@mui/material';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { useConfirmDialog } from 'src/hooks/useConfirmDialog';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import { format, addDays, differenceInCalendarDays, parse } from 'date-fns';
import { formatDateForInput } from 'src/utils/utils';
import { BaseAPI } from 'src/providers/base-api';
import { LeaveV2API, LeaveRequestBody } from 'src/providers/leave-v2-api';
import { useTenantFeatures } from 'src/hooks/useTenantFeatures';
dayjs.extend(duration);

const QuickLinksCard = () => {
  const theme = useTheme();
  const { confirm, notifySuccess, notifyError, notifyWarning, notifyInfo, Swal } = useConfirmDialog();
  // State variables
  const [wfhDialogOpen, setWfhDialogOpen] = useState(false);
  const [permissionDialogOpen, setPermissionDialogOpen] = useState(false);
  const [emergencyDialogOpen, setEmergencyDialogOpen] = useState(false);
  // Add these state variables after the other state declarations
  const [generalShiftTiming, setGeneralShiftTiming] = useState<any>(null);
  const tenantFeatures = useTenantFeatures();
  // WFH State
  const [firstHalf, setFirstHalf] = useState(true);
  const [secondHalf, setSecondHalf] = useState(true);
  const [wfhReason, setWfhReason] = useState('');
  const [wfhPreview, setWfhPreview] = useState<{ loading: boolean; blocked: boolean; message: string | null }>({
    loading: false,
    blocked: false,
    message: null
  });

  // Permission State
  const [permissionType, setPermissionType] = useState('Late Coming');
  const [permissionStart, setPermissionStart] = useState('');
  const [permissionEnd, setPermissionEnd] = useState('');
  const [permissionResume, setPermissionResume] = useState('');
  const [permissionHours, setPermissionHours] = useState('');
  const [permissionReason, setPermissionReason] = useState('');
  const [permissionHoursError, setPermissionHoursError] = useState(false);
  const [permissionHoursHelperText, setPermissionHoursHelperText] = useState('');
  const [isUpdatePermissionMode, setIsUpdatePermissionMode] = useState(false);
  const [permissionId, setPermissionId] = useState('');

  // Emergency/Sick Leave State
  const [emergencyReason, setEmergencyReason] = useState('');
  const [firstHalfEmergency, setFirstHalfEmergency] = useState(false);
  const [secondHalfEmergency, setSecondHalfEmergency] = useState(false);
  const [emergencyPreview, setEmergencyPreview] = useState<{ loading: boolean; blocked: boolean; message: string | null }>({
    loading: false,
    blocked: false,
    message: null
  });

  // Common details
  const [shiftTime, setShiftTime] = useState<any>([]);
  const [employeeSupervisorEmail, setEmployeeSupervisorEmail] = useState('');
  const [holidayCalenderList, setHolidayCalenderList] = useState<any>([]);
  const [partyId, setPartyId] = useState<string | null>(null);

  useEffect(() => {
    if (!tenantFeatures.quickLinksWFHEnabled && !tenantFeatures.quickLinksPermissionEnabled && !tenantFeatures.quickLinksEmergencyEnabled) {
      return;
    }

    const initializeData = async () => {
      const userDetail = JSON.parse(sessionStorage.getItem('UserDetail') || '{}');
      const data = JSON.parse(sessionStorage.getItem('UserpartyId'));
      const userPartyId = data || userDetail?.partyId;
      setPartyId(userPartyId);

      await getEmployeeDetail();
      await getAllHolidays();
    };

    initializeData();
  }, [tenantFeatures.quickLinksWFHEnabled, tenantFeatures.quickLinksPermissionEnabled, tenantFeatures.quickLinksEmergencyEnabled]);

  // Fetch general shift timing when tenant config loads (separate effect to avoid re-fetching all data)
  useEffect(() => {
    if (tenantFeatures.allowSupervisorMarkingAttendance) {
      fetchGeneralShiftTiming();
    }
  }, [tenantFeatures.allowSupervisorMarkingAttendance]);

  // --- Helper Functions duplicated/adapted from Notifications/index.tsx ---

  const formatTime = (time: string) => (time ? dayjs(`1970-01-01T${time}`).format('hh:mm A') : '');
  const MAX_PERMISSION_MINUTES = 120;

  const getAllHolidays = async () => {
    try {
      await BaseAPI.getAllHolidays((result: any) => {
        if (result.status === 200) {
          setHolidayCalenderList(result.data[0].holidays);
        }
      });
    } catch (error) {
      console.error(error);
    }
  };

  const fetchGeneralShiftTiming = async () => {
    try {
      await BaseAPI.getShiftTime((shiftResult: any) => {
        if (shiftResult.status === 200 && Array.isArray(shiftResult.data)) {
          const generalShift = shiftResult.data.find((data) => data.shiftType === 'G');
          if (generalShift) {
            setGeneralShiftTiming(generalShift);
          } else {
            console.warn('⚠️ General shift (G) not found in shift timings');
          }
        } else {
          console.warn('Failed to fetch shift timing data for RDS');
        }
      });
    } catch (error) {
      console.error('Error fetching general shift timing for RDS:', error);
    }
  };
  const getEmployeeDetail = async () => {
    try {
      const userPartyId = JSON.parse(sessionStorage.getItem('UserpartyId'));
      if (!userPartyId) return;

      await BaseAPI.getEmployeeByPartyID(userPartyId, async (result: any) => {
        if (result.status !== 200 || !result.data) return;

        const employeeData = result.data;
        const employeeId = employeeData?.id;
        const employeeDepartment = employeeData?.department;
        const employeeSupervisorID = employeeData?.immediateSupervisorPartyId;

        if (employeeSupervisorID) {
          await BaseAPI.getEmployeeByPartyID(parseInt(employeeSupervisorID), (empResponse: any) => {
            if (empResponse.status === 200) {
              setEmployeeSupervisorEmail(empResponse?.data?.email);
            }
          });
        }
        let employeeShiftType = 'G';

        if (employeeDepartment === 'Netops') {
          const today = new Date().toISOString().split('T')[0];
          const response = await BaseAPI.getShiftPlanByEmployeeId(employeeId);
          if (response.status === 200 && Array.isArray(response.data)) {
            const todayShift = response.data?.find((shift: any) => shift.startData === today);
            if (todayShift) {
              employeeShiftType = todayShift.shiftType;
            }
          }
        }

        await BaseAPI.getShiftTime((shiftResult: any) => {
          if (shiftResult.status === 200 && Array.isArray(shiftResult.data)) {
            const shiftTime = shiftResult.data.filter((data: any) => data.shiftType === employeeShiftType);
            setShiftTime(shiftTime);
          }
        });
      });
    } catch (error) {
      console.error('Error fetching employee detail:', error);
    }
  };

  const openWFHDialog = () => {
    setWfhReason('');
    setWfhPreview({ loading: true, blocked: false, message: null });
    setWfhDialogOpen(true);
  };

  const openPermissionDialog = () => {
    // RDS Server validation - check if permission is being applied at least 1 hour before shift start
    if (tenantFeatures.allowSupervisorMarkingAttendance) {
      // Use generalShiftTiming for RDS, not the user's assigned shift
      const rdsShiftTiming = generalShiftTiming;

      if (!rdsShiftTiming) {
        console.warn('⚠️ General shift timing not loaded yet for RDS');
        setPermissionDialogOpen(true);
        setPermissionHoursError(false);
        setPermissionHoursHelperText('');
        setPermissionType('');
        setPermissionStart('');
        setPermissionResume('');
        setPermissionEnd('');
        setPermissionHours('');
        setPermissionReason('');
        return;
      }

      const shiftStart = rdsShiftTiming.startTime || '';
      const shiftEnd = rdsShiftTiming.endTime || '';

      if (shiftStart) {
        const now = new Date();
        const currentHour = now.getHours();
        const currentMinute = now.getMinutes();
        const currentTotalMinutes = currentHour * 60 + currentMinute;

        // Parse shift start time
        const [shiftHourStr, shiftMinuteStr] = shiftStart.split(':');
        const shiftStartHour = parseInt(shiftHourStr, 10);
        const shiftStartMinute = parseInt(shiftMinuteStr, 10);
        const shiftStartTotalMinutes = shiftStartHour * 60 + shiftStartMinute;

        // Parse shift end time
        const [endHourStr, endMinuteStr] = shiftEnd.split(':');
        const shiftEndHour = parseInt(endHourStr, 10);
        const shiftEndMinute = parseInt(endMinuteStr, 10);
        const shiftEndTotalMinutes = shiftEndHour * 60 + shiftEndMinute;

        let minutesUntilShift;

        // Detect night shift scenarios
        const isTraditionalNightShift = shiftEndTotalMinutes < shiftStartTotalMinutes;
        const isEarlyMorningShift = shiftStartHour >= 0 && shiftStartHour < 6;

        if (isEarlyMorningShift && !isTraditionalNightShift) {
          // Early morning shift (e.g., 01:33 - 07:32)
          if (currentTotalMinutes >= 720) {
            // 720 = 12:00 PM
            const minutesUntilMidnight = 24 * 60 - currentTotalMinutes;
            minutesUntilShift = minutesUntilMidnight + shiftStartTotalMinutes;
          } else if (currentTotalMinutes < shiftStartTotalMinutes) {
            minutesUntilShift = shiftStartTotalMinutes - currentTotalMinutes;
          } else if (currentTotalMinutes >= shiftStartTotalMinutes && currentTotalMinutes < shiftEndTotalMinutes) {
            minutesUntilShift = -(currentTotalMinutes - shiftStartTotalMinutes);
          } else {
            minutesUntilShift = -1;
          }
        } else if (isTraditionalNightShift) {
          // Traditional night shift (e.g., 21:30 - 06:30)
          if (currentTotalMinutes >= shiftStartTotalMinutes) {
            minutesUntilShift = -(currentTotalMinutes - shiftStartTotalMinutes);
          } else if (currentTotalMinutes < shiftEndTotalMinutes) {
            minutesUntilShift = -(24 * 60 - shiftStartTotalMinutes + currentTotalMinutes);
          } else {
            minutesUntilShift = shiftStartTotalMinutes - currentTotalMinutes;
          }
        } else {
          // Regular day shift
          minutesUntilShift = shiftStartTotalMinutes - currentTotalMinutes;
        }

        // Block if current time is AT or AFTER shift start time
        if (minutesUntilShift <= 0) {
          Swal.fire({
            icon: 'warning',
            title: 'Permission Request Not Allowed',
            text: 'Permission cannot be applied after shift start time.',
            confirmButtonColor: '#4caf50'
          });
          return;
        }

        // Block if less than 60 minutes before shift start
        if (minutesUntilShift < 60) {
          const remainingMinutes = 60 - minutesUntilShift;
          Swal.fire({
            icon: 'warning',
            title: 'Permission Request Not Allowed',
            text: `Permission must be applied at least 1 hour before shift start time. Please wait ${remainingMinutes} more minute(s).`,
            confirmButtonColor: '#4caf50'
          });
          return;
        }
      }
    }

    setPermissionDialogOpen(true);
    setPermissionHoursError(false);
    setPermissionHoursHelperText('');
    setPermissionType('');
    setPermissionStart('');
    setPermissionResume('');
    setPermissionEnd('');
    setPermissionHours('');
    setPermissionReason('');
    setIsUpdatePermissionMode(false);
    setPermissionId('');
  };

  const OpenEmergencyDialog = () => {
    setEmergencyReason('');
    setEmergencyPreview({ loading: true, blocked: false, message: null });
    setEmergencyDialogOpen(true);
  };

  // --- Permission Logic ---
  useEffect(() => {
    if (!permissionStart || !permissionEnd) {
      setPermissionHours('');
      setPermissionHoursError(false);
      setPermissionHoursHelperText('');
      return;
    }
    let start = dayjs(`2023-01-01T${permissionStart}`);
    let end = dayjs(`2023-01-01T${permissionEnd}`);
    if (!end.isAfter(start)) end = end.add(1, 'day');

    const diffInMinutes = end.diff(start, 'minute');
    const hours = Math.floor(diffInMinutes / 60);
    const minutes = diffInMinutes % 60;
    const formatted = `${hours > 0 ? `${hours} hr ` : ''}${minutes > 0 ? `${minutes} min` : ''}`.trim();
    setPermissionHours(formatted);

    if (diffInMinutes > MAX_PERMISSION_MINUTES && permissionType !== 'Continue WFH') {
      setPermissionHoursError(true);
      setPermissionHoursHelperText('2hr Max');
    } else {
      setPermissionHoursError(false);
      setPermissionHoursHelperText('');
    }
  }, [permissionStart, permissionEnd, permissionType]);

  useEffect(() => {
    if (!shiftTime.length) return;
    const shiftStart = shiftTime[0]?.startTime || '';
    const shiftEnd = shiftTime[0]?.endTime || '';
    const formattedStart = formatTime(permissionStart);
    const formattedEnd = formatTime(permissionEnd);

    if (permissionType === 'Late Coming') {
      setPermissionStart(shiftStart);
      if (permissionEnd) {
        setPermissionReason(`I will reach office at ${formattedEnd}`);
      }
    } else if (permissionType === 'Leaving Early') {
      setPermissionEnd(shiftEnd);
      if (permissionStart) {
        setPermissionReason(`Unavailable from ${formattedStart}`);
      }
    } else if (permissionType === 'Continue WFH') {
      if (permissionResume) setPermissionReason('Will continue my work from home');
    }
  }, [permissionType, shiftTime, permissionEnd, permissionStart, permissionResume]);

  // --- Submissions ---

  /**
   * Runs the Leave V2 engine's preview (no persistence) for a WFH/Emergency
   * intent. Used both for the live "check on open / on selection change" UX
   * and as the first step of the actual submit flow below.
   */
  const previewLeaveV2 = async (
    body: LeaveRequestBody
  ): Promise<{ blocked: boolean; message: string | null; sandwichDays: { date: string }[] }> => {
    const preview = await LeaveV2API.previewRequest(body);
    if (preview.status !== 200 || !preview.data) {
      return { blocked: true, message: preview.message || 'Unable to validate this request.', sandwichDays: [] };
    }
    if (preview.data.effect === 'block') {
      return {
        blocked: true,
        message: (preview.data.reasons || []).join(' ') || 'This request is blocked by policy.',
        sandwichDays: []
      };
    }
    const sandwichDays = (preview.data.days || []).filter((d) => d.is_sandwich);
    return { blocked: false, message: null, sandwichDays };
  };

  /**
   * Preview → (block/sandwich-ack) → create against the Leave V2 engine.
   * Shared by WFH and Emergency/Sick quick links (both are just leave_type_code
   * variants of the same generic apply-leave flow). The engine itself rejects
   * the request if one already exists overlapping this date range (checked
   * against both lv2_request and the legacy leave tables), so no client-side
   * "already applied today" pre-check is needed — the live preview effects
   * below surface that same rejection as soon as the dialog opens instead of
   * only at submit time.
   */
  const submitLeaveV2Request = async (body: LeaveRequestBody): Promise<{ ok: boolean }> => {
    try {
      const result = await previewLeaveV2(body);
      if (result.blocked) {
        Swal.fire({
          icon: 'error',
          title: 'Request Blocked',
          text: result.message || 'This request is blocked by policy.',
          confirmButtonColor: '#4caf50'
        });
        return { ok: false };
      }

      if (result.sandwichDays.length > 0) {
        const dateList = result.sandwichDays.map((d) => d.date).join(', ');
        const confirmSandwich = await Swal.fire({
          title: 'Sandwich Leave Policy',
          html: `This request spans <strong>${result.sandwichDays.length}</strong> non-working day(s) (${dateList}) that will be counted against your balance because the leave brackets them on both sides. Continue?`,
          icon: 'warning',
          showCancelButton: true,
          confirmButtonColor: '#4caf50',
          cancelButtonText: 'Go back'
        });
        if (!confirmSandwich.isConfirmed) return { ok: false };
        body.sandwich_ack = true;
      }

      const created = await LeaveV2API.createRequest(body);
      if (created.status === 200 || created.status === 201) {
        return { ok: true };
      }

      Swal.fire('Error', created.message || 'Submission failed', 'error');
      return { ok: false };
    } catch (error) {
      console.error(error);
      Swal.fire('Error', 'Submission failed', 'error');
      return { ok: false };
    }
  };

  // Live preview: re-validate against the engine as soon as the WFH dialog is
  // open (and whenever the half-day selection changes), so a conflict/block is
  // shown immediately instead of only after the user hits Submit.
  useEffect(() => {
    if (!wfhDialogOpen) return;
    if (!firstHalf && !secondHalf) {
      setWfhPreview({ loading: false, blocked: false, message: null });
      return;
    }

    let cancelled = false;
    const portion = firstHalf && secondHalf ? 'full' : firstHalf ? 'first_half' : 'second_half';
    const today = formatDateForInput(new Date());

    setWfhPreview((prev) => ({ ...prev, loading: true }));
    const timer = setTimeout(async () => {
      const result = await previewLeaveV2({
        employee_id: 0,
        leave_type_code: 'work_from_home',
        start_date: today,
        end_date: today,
        days: [{ date: today, portion }],
        is_self_requested: true
      });
      if (!cancelled) {
        setWfhPreview({ loading: false, blocked: result.blocked, message: result.message });
      }
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [wfhDialogOpen, firstHalf, secondHalf]);

  // Same live preview for Emergency/Sick Leave.
  useEffect(() => {
    if (!emergencyDialogOpen) return;
    if (!firstHalfEmergency && !secondHalfEmergency) {
      setEmergencyPreview({ loading: false, blocked: false, message: null });
      return;
    }

    let cancelled = false;
    const portion = firstHalfEmergency && secondHalfEmergency ? 'full' : firstHalfEmergency ? 'first_half' : 'second_half';
    const today = formatDateForInput(new Date());

    setEmergencyPreview((prev) => ({ ...prev, loading: true }));
    const timer = setTimeout(async () => {
      const result = await previewLeaveV2({
        employee_id: 0,
        leave_type_code: 'sick_leave',
        start_date: today,
        end_date: today,
        days: [{ date: today, portion }],
        is_self_requested: true
      });
      if (!cancelled) {
        setEmergencyPreview({ loading: false, blocked: result.blocked, message: result.message });
      }
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [emergencyDialogOpen, firstHalfEmergency, secondHalfEmergency]);

  const submitWFHRequest = async () => {
    setWfhDialogOpen(false);
    const today = formatDateForInput(new Date());

    if (!firstHalf && !secondHalf) {
      await Swal.fire({ icon: 'warning', title: 'Invalid Selection', text: 'Select at least one half.' });
      return;
    }

    const wfhTypeText = firstHalf && secondHalf ? 'FULL DAY' : firstHalf ? '1ST HALF' : '2ND HALF';
    const portion = firstHalf && secondHalf ? 'full' : firstHalf ? 'first_half' : 'second_half';

    const confirm = await Swal.fire({
      title: 'Confirm WFH Request',
      html: `Requesting WFH for <strong>today</strong> as <strong>${wfhTypeText}</strong> due to <strong>${wfhReason}</strong>?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#4caf50',
      confirmButtonText: 'Apply'
    });

    if (!confirm.isConfirmed) return;

    const body: LeaveRequestBody = {
      employee_id: 0,
      leave_type_code: 'work_from_home',
      start_date: today,
      end_date: today,
      days: [{ date: today, portion }],
      reason: wfhReason,
      is_self_requested: true,
      is_quick_link: true
    };

    const result = await submitLeaveV2Request(body);
    if (result.ok) {
      await Swal.fire({ icon: 'success', title: 'WFH Request Submitted', confirmButtonColor: '#4caf50' });
    }
  };

  // Looks up whether the employee already has a permission of this type submitted
  // for today, so the dialog can switch into update mode instead of creating a
  // duplicate. Routes through the Leave V2 engine (leave_type_code='permission')
  // instead of the legacy /permissions_data endpoint — that endpoint is served
  // by the NestJS backend only, which isn't reachable at all once the frontend
  // points directly at the Go backend (no Traefik/NestJS proxy in front of it),
  // so it always 502s in that setup. lv2_request is also the only place a
  // permission created via this dialog actually lives now (see submitPermissionRequest).
  const getPermissioByEmployeeIdData = async (selectedType: string) => {
    const today = formatDateForInput(new Date());
    try {
      const response = await LeaveV2API.listMyRequests({ leave_type: 'permission', from: today, to: today, limit: 50 });
      if (response.status !== 200 || !response.data) return null;

      const matched = response.data.data.find(
        (req) => req.permission_type === selectedType && req.status !== 'cancelled' && req.status !== 'declined'
      );
      if (!matched) return null;

      const day = matched.days?.[0];
      const isContinueWFH = selectedType === 'Continue WFH';
      return {
        id: matched.id,
        startTime: isContinueWFH ? '' : (day?.start_time ?? ''),
        endTime: isContinueWFH ? '' : (day?.end_time ?? ''),
        resumingTime: isContinueWFH ? (day?.end_time ?? '') : '',
        reason: matched.reason ?? ''
      };
    } catch (error) {
      console.error('Error fetching employee permission data:', error);
      return null;
    }
  };

  const updatePermissioById = async () => {
    setPermissionDialogOpen(false);
    try {
      const today = formatDateForInput(new Date());
      const isContinueWFH = permissionType === 'Continue WFH';
      const startTime = isContinueWFH ? (shiftTime[0]?.endTime || '') : permissionStart;
      const endTime = isContinueWFH ? permissionResume : permissionEnd;

      const body: LeaveRequestBody = {
        employee_id: 0,
        leave_type_code: 'permission',
        start_date: today,
        end_date: today,
        days: [{ date: today, portion: 'hours', start_time: startTime, end_time: endTime }],
        reason: permissionReason,
        is_self_requested: true,
        permission_type: permissionType
      };

      const response = await LeaveV2API.updateRequest(Number(permissionId), body);
      if (response.status === 200) {
        Swal.fire({ title: 'Updated', icon: 'success', confirmButtonText: 'OK', confirmButtonColor: '#4caf50' });
      } else {
        console.error('Update failed:', response);
        Swal.fire('Error', response.message || 'Failed to update permission.', 'error');
      }
    } catch (error) {
      console.error('Unexpected error during update:', error);
      Swal.fire('Error', 'Update failed', 'error');
    } finally {
      setIsUpdatePermissionMode(false);
    }
  };

  // Routes through the Leave V2 engine (leave_type_code='permission') instead of
  // the legacy /permissions_data endpoint, so permission_type_config policy rules
  // (approval toggle, max duration, auto-approve threshold) actually apply here —
  // the legacy endpoint has no knowledge of the policy engine at all.
  const submitPermissionRequest = async () => {
    setPermissionDialogOpen(false);

    const confirm = await Swal.fire({
      title: 'Confirm Permission',
      html: `Apply <strong>${permissionHours}</strong> for ${permissionType}?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#4caf50',
      confirmButtonText: 'Yes'
    });

    if (!confirm.isConfirmed) return;

    const today = formatDateForInput(new Date());
    const isContinueWFH = permissionType === 'Continue WFH';
    // Continue WFH has no explicit start time in this dialog (only "Resuming
    // Time") — the permission window is [shift end, resuming time), matching
    // "continuing to work from home past shift end until X".
    const startTime = isContinueWFH ? (shiftTime[0]?.endTime || '') : permissionStart;
    const endTime = isContinueWFH ? permissionResume : permissionEnd;

    const body: LeaveRequestBody = {
      employee_id: 0,
      leave_type_code: 'permission',
      start_date: today,
      end_date: today,
      days: [{ date: today, portion: 'hours', start_time: startTime, end_time: endTime }],
      reason: permissionReason,
      is_self_requested: true,
      permission_type: permissionType,
      is_quick_link: true
    };

    const result = await submitLeaveV2Request(body);
    if (result.ok) {
      Swal.fire({ icon: 'success', title: 'Permission submitted successfully', confirmButtonColor: '#4caf50' });
    }
  };
  const submitEmergencyLeaveRequest = async () => {
    setEmergencyDialogOpen(false);
    const today = formatDateForInput(new Date());

    if (!firstHalfEmergency && !secondHalfEmergency) {
      await Swal.fire({ icon: 'warning', title: 'Invalid Selection', text: 'Select at least one half.' });
      return;
    }

    const typeText = firstHalfEmergency && secondHalfEmergency ? 'FULL DAY' : firstHalfEmergency ? '1ST HALF' : '2ND HALF';
    const portion = firstHalfEmergency && secondHalfEmergency ? 'full' : firstHalfEmergency ? 'first_half' : 'second_half';

    const { isConfirmed } = await Swal.fire({
      title: 'Confirm Emergency Leave',
      html: `Apply for <strong>${typeText}</strong> leave on <strong>today</strong>?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#4caf50',
      confirmButtonText: 'Apply'
    });

    if (!isConfirmed) return;

    const body: LeaveRequestBody = {
      employee_id: 0,
      leave_type_code: 'sick_leave',
      start_date: today,
      end_date: today,
      days: [{ date: today, portion }],
      reason: emergencyReason,
      is_self_requested: true,
      is_quick_link: true
    };

    const result = await submitLeaveV2Request(body);
    if (result.ok) {
      await Swal.fire({ icon: 'success', title: 'Request Submitted', confirmButtonColor: '#4caf50' });
    }
  };

  return (
    <>
      <Card sx={{ height: '100%', p: 2 }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          Quick Link
        </Typography>
        <MenuList>
          {tenantFeatures.quickLinksWFHEnabled && (
            <MenuItem onClick={openWFHDialog} sx={{ borderRadius: 1 }}>
              <ListItemIcon>
                <HomeWorkIcon fontSize="medium" color="primary" />
              </ListItemIcon>
              <ListItemText primary="Work From Home" />
            </MenuItem>
          )}

          {tenantFeatures.quickLinksPermissionEnabled && (
            <MenuItem onClick={openPermissionDialog} sx={{ borderRadius: 1 }}>
              <ListItemIcon>
                <AccessTimeIcon fontSize="medium" color="primary" />
              </ListItemIcon>
              <ListItemText primary="Permission" />
            </MenuItem>
          )}

          {tenantFeatures.quickLinksEmergencyEnabled && (
            <MenuItem onClick={OpenEmergencyDialog} sx={{ borderRadius: 1 }}>
              <ListItemIcon>
                <MedicalServicesIcon fontSize="medium" color="primary" />
              </ListItemIcon>
              <ListItemText primary="Emergency/Sick Leave" />
            </MenuItem>
          )}
        </MenuList>
      </Card>

      {/* WFH Dialog */}
      <Dialog open={wfhDialogOpen} onClose={() => setWfhDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ backgroundColor: '#33C2FF', color: 'white' }}>Apply Work From Home</DialogTitle>
        <DialogContent dividers>
          {wfhPreview.blocked && wfhPreview.message && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {wfhPreview.message}
            </Alert>
          )}
          <TextField
            fullWidth
            label="Date"
            value={new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
            margin="normal"
            InputProps={{ readOnly: true }}
            disabled
          />
          <Box sx={{ display: 'flex', flexDirection: 'row', mb: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={firstHalf}
                  onChange={(e) => setFirstHalf(e.target.checked)}
                  style={{ color: '#57CA22' }}
                />
              }
              label="1st Half"
              sx={{ mr: 4 }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={secondHalf}
                  onChange={(e) => setSecondHalf(e.target.checked)}
                  style={{ color: '#57CA22' }}
                />
              }
              label="2nd Half"
            />
          </Box>
          <TextField
            fullWidth
            label="Reason"
            placeholder="Enter reason..."
            multiline
            rows={3}
            required
            value={wfhReason}
            onChange={(e) => setWfhReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWfhDialogOpen(false)} color="info" variant="contained">
            Cancel
          </Button>
          <Button
            onClick={submitWFHRequest}
            color="success"
            variant="contained"
            disabled={(!firstHalf && !secondHalf) || !wfhReason.trim() || wfhPreview.blocked || wfhPreview.loading}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      {/* Permission Dialog */}
      <Dialog open={permissionDialogOpen} onClose={() => setPermissionDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ backgroundColor: '#33C2FF', color: 'white' }}>Apply Permission</DialogTitle>
        <DialogContent dividers>
          {/* ... (Ported grid layout from Notifications) ... */}
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box display="flex" alignItems="center">
                  <CalendarTodayIcon sx={{ fontSize: 18, color: 'gray', mr: 1 }} />
                  <Typography variant="body2">{dayjs().format('DD-MMM-YYYY')}</Typography>
                </Box>
                <Box display="flex" alignItems="center">
                  <AccessTimeIcon sx={{ fontSize: 18, color: 'gray', mr: 1 }} />
                  <Typography variant="body2">
                    <strong>{shiftTime[0]?.shiftType || 'N/A'}</strong> – {formatTime(shiftTime[0]?.startTime)} to{' '}
                    {formatTime(shiftTime[0]?.endTime)}
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <FormControl component="fieldset">
                <RadioGroup
                  row
                  value={permissionType}
                  onChange={async (e) => {
                    const selectedType = e.target.value;
                    setPermissionType(selectedType);
                    setPermissionHoursError(false);
                    setPermissionHoursHelperText('');
                    setPermissionStart('');
                    setPermissionResume('');
                    setPermissionEnd('');
                    setPermissionReason('');

                    const existing = await getPermissioByEmployeeIdData(selectedType);
                    if (existing) {
                      setIsUpdatePermissionMode(true);
                      setPermissionStart(existing.startTime);
                      setPermissionEnd(existing.endTime);
                      setPermissionResume(existing.resumingTime);
                      setPermissionReason(existing.reason);
                      const start = dayjs(`2023-01-01T${existing.startTime}`);
                      const end = dayjs(`2023-01-01T${existing.endTime}`);
                      if (end.isAfter(start)) {
                        const diff = end.diff(start, 'minute');
                        const hrs = Math.floor(diff / 60);
                        const mins = diff % 60;
                        const formatted = `${hrs > 0 ? `${hrs} hr ` : ''}${mins > 0 ? `${mins} min` : ''}`.trim();
                        setPermissionHours(formatted);
                      }
                      setPermissionId(String(existing.id));
                    } else {
                      setIsUpdatePermissionMode(false);
                      setPermissionHours('');
                    }
                  }}
                >
                  <FormControlLabel value="Late Coming" control={<Radio style={{ color: '#57CA22' }} />} label="Late Coming" />
                  <FormControlLabel value="Leaving Early" control={<Radio style={{ color: '#57CA22' }} />} label="Leaving Early" />
                  <FormControlLabel value="Permission" control={<Radio style={{ color: '#57CA22' }} />} label="During Work hours" />
                  {tenantFeatures.quickLinksWFHEnabled && (
                    <FormControlLabel
                      value="Continue WFH"
                      control={<Radio style={{ color: '#57CA22' }} />}
                      label="Continue Work From Home"
                    />
                  )}{' '}
                </RadioGroup>
              </FormControl>
            </Grid>
            {/* Time Inputs */}
            {permissionType !== 'Continue WFH' ? (
              <>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label={permissionType === 'Leaving Early' ? 'Leaving Time' : 'From Time'}
                    type="time"
                    value={permissionStart}
                    InputLabelProps={{ shrink: true }}
                    onChange={(e) => setPermissionStart(e.target.value)}
                    disabled={permissionType === 'Late Coming'}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label={permissionType === 'Late Coming' ? 'Reaching Time' : 'To Time'}
                    type="time"
                    value={permissionEnd}
                    InputLabelProps={{ shrink: true }}
                    onChange={(e) => setPermissionEnd(e.target.value)}
                    disabled={permissionType === 'Leaving Early'}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="No. of Hours"
                    value={permissionHours}
                    disabled
                    error={permissionHoursError}
                    helperText={permissionHoursHelperText}
                  />
                </Grid>
              </>
            ) : (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Resuming Time"
                  type="time"
                  value={permissionResume}
                  InputLabelProps={{ shrink: true }}
                  onChange={(e) => setPermissionResume(e.target.value)}
                />
              </Grid>
            )}

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Reason"
                value={permissionReason}
                onChange={(e) => setPermissionReason(e.target.value)}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setPermissionDialogOpen(false);
              setIsUpdatePermissionMode(false);
            }}
            color="info"
            variant="contained"
          >
            Cancel
          </Button>
          <Button
            onClick={isUpdatePermissionMode ? updatePermissioById : submitPermissionRequest}
            color="success"
            variant="contained"
            disabled={!permissionType || !permissionReason.trim() || permissionHoursError}
          >
            {isUpdatePermissionMode ? 'Update' : 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Emergency Dialog */}
      <Dialog open={emergencyDialogOpen} onClose={() => setEmergencyDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ backgroundColor: '#33C2FF', color: 'white' }}>Apply Emergency/Sick Leave</DialogTitle>
        <DialogContent dividers>
          {emergencyPreview.blocked && emergencyPreview.message && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {emergencyPreview.message}
            </Alert>
          )}
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Emergency Reason</InputLabel>
                <Select value={emergencyReason} label="Emergency Reason" onChange={(e) => setEmergencyReason(e.target.value)}>
                  <MenuItem value="Emergency Travel">Emergency Travel</MenuItem>
                  <MenuItem value="I am not well ">I am not well</MenuItem>
                  <MenuItem value="My family members are not well ">My family members are not well</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormGroup row>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={firstHalfEmergency}
                      onChange={(e) => setFirstHalfEmergency(e.target.checked)}
                      style={{ color: '#57CA22' }}
                    />
                  }
                  label="1st Half"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={secondHalfEmergency}
                      onChange={(e) => setSecondHalfEmergency(e.target.checked)}
                      style={{ color: '#57CA22' }}
                    />
                  }
                  label="2nd Half"
                />
              </FormGroup>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEmergencyDialogOpen(false)} color="info" variant="contained">
            Cancel
          </Button>
          <Button
            onClick={submitEmergencyLeaveRequest}
            color="success"
            variant="contained"
            disabled={
              !emergencyReason ||
              (!firstHalfEmergency && !secondHalfEmergency) ||
              emergencyPreview.blocked ||
              emergencyPreview.loading
            }
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default QuickLinksCard;
